import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as ShipAidLogo } from '../assets/icons/ShipAid.svg';
import { useLazyQuery } from '@apollo/client';
import {
  XIcon,
  ShoppingBagIcon,
  HomeIcon,
  BellIcon,
  UsersIcon,
  UserCircleIcon,
  CogIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  RefreshIcon,
  PencilAltIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  TruckIcon,
  CubeIcon,
} from '@heroicons/react/outline';
import { SidebarNavLink } from './SidebarNavLink';
import { SidebarNavDiv } from './SidebarNavDiv';
import { useUser } from 'context/user-context';
import { useStore } from 'context/store-context';
import { Text } from './Text';
import { GET_ORDERS_CLAIMS_COUNT } from 'gql/queries';
import { isShipaidAdmin } from 'utils';
import { canManageRequests } from 'types/resolution';
import {
  getRequestReportsHref,
  getRequestsHref,
} from 'utils/requestReportNavigation';
import classNames from 'classnames';
import { SidebarToggleIcon } from './SidebarToggleIcon';
import { useCurrentStaffAccess } from 'hooks/useCurrentStaffAccess';

type SidebarProps = {
  sidebarOpen: boolean;
  setSidebarOpen: (value: boolean) => void;
  isCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
};

export const SideBar = ({
  sidebarOpen,
  setSidebarOpen,
  isCollapsed: externalIsCollapsed,
  onCollapseChange,
}: SidebarProps) => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const adminData = localStorage.getItem(
    process.env.REACT_APP_ADMIN_STORAGE_KEY || 'admin-context',
  );
  let adminContext: any;
  if (adminData) adminContext = JSON.parse(adminData);

  const [claimsCount, setClaimsCount] = useState(0);

  // Use external state if provided, otherwise use internal state with localStorage
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  const isCollapsed =
    externalIsCollapsed !== undefined
      ? externalIsCollapsed
      : internalIsCollapsed;

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    if (onCollapseChange) {
      onCollapseChange(newCollapsed);
    } else {
      setInternalIsCollapsed(newCollapsed);
      localStorage.setItem('sidebar-collapsed', String(newCollapsed));
    }
  };

  const { user, role } = useUser();
  const staffAccess = useCurrentStaffAccess();
  const isUserShipaidAdmin = isShipaidAdmin(user);
  const hasRequestsWorkspace = canManageRequests(role, isUserShipaidAdmin);
  const store = useStore();

  const [getTotalOrderandClaims] = useLazyQuery(GET_ORDERS_CLAIMS_COUNT, {
    onCompleted: (data) => {
      setClaimsCount(data.totalClaims?.aggregate.count);
    },
  });

  useEffect(() => {
    if (
      store.storeId &&
      store.storeDomain &&
      !isUserShipaidAdmin &&
      !hasRequestsWorkspace
    ) {
      getTotalOrderandClaims({
        variables: {
          storeId: store.storeId,
        },
      });
    }
  }, [
    getTotalOrderandClaims,
    store.storeId,
    store.storeDomain,
    isUserShipaidAdmin,
    hasRequestsWorkspace,
  ]);

  const prodNav = [
    {
      name: 'Home',
      icon: HomeIcon,
      to: '/',
      count: 0,
      key: 'home',
    },
    ...(hasRequestsWorkspace
      ? [
          {
            name: 'Requests',
            icon: ExclamationCircleIcon,
            to: getRequestsHref(routerLocation),
            activePaths: ['/orders', '/claims', '/returns'],
            inactivePaths: ['/requests/reports', '/requests/billing'],
            count: 0,
            key: 'requests',
          },
          {
            name: 'Reports',
            icon: ChartBarIcon,
            to: getRequestReportsHref(store.storeId, routerLocation),
            activePaths: ['/requests/billing'],
            count: 0,
            key: 'reports',
          },
        ]
      : [
          {
            name: 'Orders',
            icon: ShoppingBagIcon,
            to: 'orders',
            count: '0',
            key: 'orders',
          },
          {
            name: 'Issues',
            icon: ExclamationCircleIcon,
            to: 'claims',
            count: claimsCount,
            key: 'claims',
          },
          {
            name: 'Returns & Exchanges',
            icon: RefreshIcon,
            to: 'returns',
            count: 0,
            key: 'returns',
          },
        ]),
    {
      name: 'Order Editing',
      icon: PencilAltIcon,
      to: '/features/order-editing',
      count: 0,
      key: 'feature-order-editing',
    },
    {
      name: 'Returns & Exchanges',
      icon: RefreshIcon,
      to: '/features/returns-exchanges',
      count: 0,
      key: 'feature-returns-exchanges',
    },
    {
      name: 'Delivery Guarantee',
      icon: ShieldCheckIcon,
      to: '/features/delivery-guarantee',
      count: 0,
      key: 'feature-delivery-guarantee',
    },
    {
      name: 'Impact',
      icon: GlobeAltIcon,
      to: '/features/impact',
      count: 0,
      key: 'feature-impact',
    },
    {
      name: 'Shipping Rates',
      icon: TruckIcon,
      to: '/features/shipping-rates',
      count: 0,
      key: 'feature-shipping-rates',
    },
    {
      name: 'Fulfillment',
      icon: CubeIcon,
      to: '/features/fulfillment',
      count: 0,
      key: 'feature-fulfillment',
    },
    // {
    //   name: 'Settings',
    //   icon: AdjustmentsIcon,
    //   to: 'settings',
    //   key: 'settings',
    //   children: [
    //     {
    //       name: 'Guarantee',
    //       icon: ShieldCheckIcon,
    //       to: 'settings/protection',
    //       key: 'protection',
    //     },
    //     {
    //       name: 'Impact',
    //       icon: GiftIcon,
    //       to: 'settings/impact',
    //       key: 'impact',
    //     },
    //     {
    //       name: 'Resolution Portal',
    //       icon: DoorOpenIcon,
    //       to: 'settings/customer',
    //       key: 'customer',
    //       children: [
    //         {
    //           name: 'General',
    //           icon: CogIcon,
    //           to: 'settings/resolution-portal/general',
    //           key: 'resolution-portal/general',
    //         },
    //         {
    //           name: 'Portal',
    //           icon: DoorOpenIcon,
    //           to: 'settings/resolution-portal/portal',
    //           key: 'resolution-portal/portal',
    //         },
    //         {
    //           name: 'Resolutions',
    //           icon: HandIcon,
    //           to: 'settings/resolution-portal/resolutions',
    //           key: 'resolution-portal/resolutions',
    //         },
    //         {
    //           name: 'Media',
    //           icon: CameraIcon,
    //           to: 'settings/resolution-portal/media',
    //           key: 'resolution-portal/media',
    //         },
    //       ],
    //     },
    //     {
    //       name: 'Order Edit',
    //       icon: ShoppingCartIcon,
    //       to: 'order-settings',
    //       key: 'order-edit',
    //     },
    //     {
    //       name: 'Notifications',
    //       icon: BellIcon,
    //       to: 'settings/notifications',
    //       key: 'notifications',
    //     },
    //     {
    //       name: 'Tracking',
    //       icon: InboxIcon,
    //       to: 'settings/tracking-templates',
    //       key: 'tracking-templates',
    //     },
    //     {
    //       name: 'Automations',
    //       icon: SparklesIcon,
    //       to: 'settings/claim-automations',
    //       key: 'claim-automations',
    //     },
    //     { name: 'Team', icon: UserGroupIcon, to: 'settings/team', key: 'team' },
    //     {
    //       name: 'Account',
    //       icon: DocumentReportIcon,
    //       to: 'settings/account',
    //       key: 'account',
    //     },
    //     {
    //       name: 'Integrations',
    //       icon: ArrowSmRightIcon,
    //       to: 'settings/integration',
    //       key: 'integration',
    //     },
    //   ],
    // },
    // { name: 'Billing', icon: CashIcon, to: 'billing', key: 'billing' },
    // {
    //   name: 'Support',
    //   icon: QuestionMarkCircleIcon,
    //   to: 'support',
    //   key: 'support',
    //   children: [
    //     {
    //       name: 'Knowledge base',
    //       to: 'https://help.shipaid.com',
    //       key: 'knowledge-base-link',
    //       icon: BookOpenIcon,
    //       target: '_new',
    //     },
    //     {
    //       name: 'Chat with us',
    //       key: 'chat-with-us',
    //       icon: ChatIcon,
    //       onClick: () => {
    //         if (typeof window !== 'undefined' && window.$crisp) {
    //           window.$crisp.push(['do', 'chat:open']);
    //         } else {
    //           console.warn('Crisp chatbox script not yet loaded.');
    //         }
    //       },
    //     },
    //     {
    //       name: 'Schedule Support Call',
    //       to: 'https://calendly.com/shipaid-support/expert-call',
    //       key: 'schedule-support-call',
    //       icon: CalendarIcon,
    //       target: '_new',
    //     },
    //   ],
    // },
  ];

  const navigation =
    process.env.NODE_ENV === 'production' ? prodNav : [...prodNav];

  const adminNavigation = [
    { name: 'Merchants', icon: UsersIcon, to: 'merchants', key: 'merchants' },
    ...(staffAccess.allowed
      ? [{ name: 'Widget status', icon: ShieldCheckIcon, to: 'widget-status', key: 'widget-status' }] : []),
    { name: 'Admins', icon: UserCircleIcon, to: 'admins', key: 'admins' },
    {
      name: 'Announcements',
      icon: BellIcon,
      to: 'announcements',
      key: 'announcements',
    },
    {
      name: 'Install Requests',
      icon: UserCircleIcon,
      to: 'install-requests',
      key: 'install-requests',
    },
  ];

  return (
    <div>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div
              className={classNames(
                'relative flex-1 flex flex-col max-w-xs w-full bg-[#1e293b]',
                adminContext?.admin?.user?.user?.id && 'mt-24',
              )}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-white focus:ring-inset"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex items-center flex-shrink-0 text-center px-5 gap-2">
                  <ShipAidLogo className="text-gray-50 w-12" />
                  <span className="text-gray-50 font-bold text-xl">
                    SHIPAID
                  </span>
                </div>
                <nav className="mt-5 space-y-0">
                  {(isUserShipaidAdmin ? adminNavigation : navigation).map(
                    (item: any) =>
                      item ? (
                        <Fragment key={item.key}>
                          {item.key === 'feature-order-editing' && (
                            <h2 className="px-5 pt-3 pb-1 text-[10.2px] leading-4 font-semibold uppercase tracking-wider text-slate-400">
                              Products
                            </h2>
                          )}
                          {item.children ? (
                            <SidebarNavDiv
                              item={item}
                              setSidebarOpen={setSidebarOpen}
                            />
                          ) : (
                            <SidebarNavLink
                              item={item}
                              setSidebarOpen={setSidebarOpen}
                            />
                          )}
                        </Fragment>
                      ) : null,
                  )}
                </nav>
              </div>
              <div className="flex-shrink-0 flex flex-col border-t border-slate-700/50 px-2">
                <div
                  onClick={() => {
                    // Save current route before navigating to settings
                    const currentPath = routerLocation.pathname;
                    const isInSettings =
                      currentPath.startsWith('/settings') ||
                      currentPath.startsWith('/order-settings');
                    if (!isInSettings) {
                      sessionStorage.setItem(
                        'settingsReturnRoute',
                        currentPath,
                      );
                    }
                    navigate('/settings/protection');
                  }}
                  className="w-full bg-slate-700/50 mb-2 rounded-md text-white flex gap-2 items-center px-3 py-2.5 min-h-[44px] text-[11.9px] leading-4 font-medium justify-start cursor-pointer hover:bg-slate-600/60 transition-colors"
                >
                  <CogIcon className="w-6 h-6 flex-shrink-0" />{' '}
                  <span className="truncate">Settings</span>
                </div>
              </div>
              <div className="flex justify-center items-center pb-2">
                <a
                  href="https://www.shipaid.com/terms-of-service"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Text
                    value="Terms of Service"
                    type="xs"
                    className="text-gray-500 mr-2"
                  />
                </a>
                <Text value=" | " className="text-gray-500 mr-2" />
                <a
                  href="https://www.shipaid.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Text
                    value="Privacy Policy"
                    type="xs"
                    className="text-gray-500"
                  />
                </a>
              </div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14">
            {/* Force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div
        className={classNames(
          'hidden md:flex md:flex-col md:fixed md:inset-y-0 z-10 transition-all duration-300 ease-in-out',
          isCollapsed ? 'md:w-16' : 'md:w-72',
          adminContext?.admin?.user?.user?.id && 'mt-8',
        )}
      >
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex-1 flex flex-col min-h-0 bg-[#1e293b] border-r border-slate-700/50 shadow-lg">
          {/* Toggle Button */}
          <div className="flex items-center justify-end p-2">
            <button
              type="button"
              onClick={handleToggleCollapse}
              className="p-1.5 rounded-md text-slate-300 hover:text-slate-100 hover:bg-slate-700/50 transition-colors"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <SidebarToggleIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div
              className={classNames(
                'flex items-center flex-shrink-0 text-center gap-2',
                isCollapsed ? 'px-2 justify-center' : 'px-5',
              )}
              title={isCollapsed ? 'SHIPAID' : undefined}
            >
              <ShipAidLogo className="text-gray-50 w-12 flex-shrink-0" />
              {!isCollapsed && (
                <span className="text-gray-50 font-bold text-xl">SHIPAID</span>
              )}
            </div>
            <nav className="mt-5 flex-1 space-y-0">
              {(isUserShipaidAdmin ? adminNavigation : navigation).map(
                (item: any) =>
                  item ? (
                    <Fragment key={item.key}>
                      {item.key === 'feature-order-editing' && (
                        <h2
                          className={
                            isCollapsed
                              ? 'sr-only'
                              : 'px-5 pt-3 pb-1 text-[10.2px] leading-4 font-semibold uppercase tracking-wider text-slate-400'
                          }
                        >
                          Products
                        </h2>
                      )}
                      {item.children ? (
                        <SidebarNavDiv
                          item={item}
                          isCollapsed={isCollapsed}
                          onExpandSidebar={handleToggleCollapse}
                        />
                      ) : (
                        <SidebarNavLink item={item} isCollapsed={isCollapsed} />
                      )}
                    </Fragment>
                  ) : null,
              )}
            </nav>
          </div>
          <div className="flex-shrink-0 flex flex-col border-t border-slate-700/50 px-2">
            {!isCollapsed ? (
              <div
                onClick={() => {
                  // Save current route before navigating to settings
                  const currentPath = routerLocation.pathname;
                  const isInSettings =
                    currentPath.startsWith('/settings') ||
                    currentPath.startsWith('/order-settings');
                  if (!isInSettings) {
                    sessionStorage.setItem('settingsReturnRoute', currentPath);
                  }
                  navigate('/settings/protection');
                }}
                className="w-full bg-slate-700/50 mb-2 rounded-md text-white flex gap-2 items-center px-3 py-2.5 min-h-[44px] text-[11.9px] leading-4 font-medium justify-start cursor-pointer hover:bg-slate-600/60 transition-colors"
              >
                <CogIcon className="w-6 h-6 flex-shrink-0" />{' '}
                <span className="truncate">Settings</span>
              </div>
            ) : (
              <div
                onClick={() => {
                  // Save current route before navigating to settings
                  const currentPath = routerLocation.pathname;
                  const isInSettings =
                    currentPath.startsWith('/settings') ||
                    currentPath.startsWith('/order-settings');
                  if (!isInSettings) {
                    sessionStorage.setItem('settingsReturnRoute', currentPath);
                  }
                  navigate('/settings/protection');
                }}
                className="w-full mb-2 rounded-md text-white flex gap-2 items-center justify-center px-2 py-2.5 min-h-[44px] text-[11.9px] leading-4 font-medium cursor-pointer bg-slate-700/50 hover:bg-slate-600/60 transition-colors"
                title="Settings"
              >
                <CogIcon className="w-6 h-6 flex-shrink-0" />
              </div>
            )}
          </div>
          <div className="flex justify-center items-center pb-2">
            <a
              href="https://www.shipaid.com/terms-of-service"
              target="_blank"
              rel="noreferrer"
              title={isCollapsed ? 'Terms of Service' : undefined}
            >
              <Text
                value={isCollapsed ? 'ToS' : 'Terms of Service'}
                type="xs"
                className="text-gray-500 mr-2"
              />
            </a>
            {!isCollapsed && (
              <Text value=" | " className="text-gray-500 mr-2" />
            )}
            <a
              href="https://www.shipaid.com/privacy-policy"
              target="_blank"
              rel="noreferrer"
              title={isCollapsed ? 'Privacy Policy' : undefined}
            >
              <Text
                value={isCollapsed ? 'PP' : 'Privacy Policy'}
                type="xs"
                className="text-gray-500"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
